/* === norsu — keep the cart's free-shipping claim honest =================
   Bug (2026-09-23): a $380 framed original showed "You've unlocked free
   domestic shipping" while checkout charged $50.

   How norsu shipping actually prices. Advanced Shipping Rules is the only AU
   rate provider on the General profile, and it applies a PER-PRODUCT surcharge,
   MULTIPLIED BY QUANTITY, summed across the cart. Ordinary goods pay $11.95
   under the $199 threshold and $0 at or above it; artwork adds its own flat
   amount on top and never counts toward the threshold. Measured 2026-09-24:

     Sideline 50x70 framed      x1 $0    x2 $0
     Newport Hoops A3 framed    x1 $20   x2 $40
     Courtside A3 framed        x1 $20   x2 $40   x3 $60
     Sunset Skating             x1 $50   x2 $100  x5 $250
     Jen Sievers LE print       x1 $50
     Tracey Mock original       x1 $50

   The surcharge is flat nationwide — Courtside x1 quotes $20 to Melbourne 3188,
   Sydney 2000 and remote WA 6770 alike. Verified against real order 143591
   ($749 to Hampton VIC): $20 + $20 + $0 + $11.95 standard = $51.95, exactly what
   was charged.

   The Liquid bar in snippets/cart-shipping-countdown.liquid knows none of this.
   It compares cart.total_price to settings.free_shipping_threshold and nothing
   else. The gate in snippets/cart-form.liquid meant to suppress it reads
   product.metafields.custom.shipping_default, which is set on only ~2,268
   products and is absent from most artwork, so the gate silently passes.
   Widening that gate is a dead end — it has drifted twice, and no product-level
   signal in this catalogue is trustworthy (the Tracey Mock originals are product
   type "Store Pickup"; prints span "Prints", "Affordable Prints", "Framed art"
   and "Posters, Prints, & Visual Artwork"). A per-item, per-cart rate cannot be
   re-derived from a product flag anyway.

   What this does instead. The drawer already prints the real ASR quote in the
   "Estimated shipping" row (the estimator at the end of assets/theme.js, which
   geolocates a postcode and calls /cart/shipping_rates.json). That figure is the
   one checkout will charge, so it settles the question:

     qualified  ("You've unlocked free domestic shipping")
                -> only allowed to stand when the estimate resolves to Free.
                   Otherwise it states the real cost. Before the estimate
                   resolves it states the offer rather than promising it.

     near       ("Spend $X more for free domestic shipping" + progress bar)
                -> a promise that spending more ends in free shipping. False
                   whenever the cart carries a surcharge, because the surcharge
                   survives any amount of extra spend. Replaced with the real
                   cost, and the progress bar hidden, ONLY when a surcharge is
                   detected — an ordinary small cart keeps its nudge, which is
                   both true and doing real work.

   Detecting "carries a surcharge" without a product flag: below the threshold
   an ordinary cart pays exactly the standard parcel rate, so anything dearer is
   a surcharge. STANDARD_UNDER is that rate and is the one number here that has
   to track ASR — if ordinary domestic postage stops being $11.95, update it.

   'far' is deliberately left alone: it states the offer as a general fact
   ("Free shipping on domestic orders over $199"), which is true regardless of
   what is in the cart, and it is the $199 nudge on small carts.

   'nz', 'gw' and 'eth' are never touched, so the New Zealand and the
   GlobeWest / Ethnicraft Melbourne-metro messaging render exactly as authored.

   Reading the rendered estimate rather than re-fetching rates is deliberate: it
   costs no extra request and it inherits the metro handling already built into
   that estimator. Do not bulk-probe /cart/shipping_rates.json to re-check any of
   this — ~40 rapid calls earn a 429 and then a bot-challenge, and throttled
   replies come back as plausible but wrong numbers.

   Loaded from snippets/cart-mini.liquid — the static drawer shell. It must not
   move into the AJAX-injected cart markup, where <script> tags never execute. */
(function () {
  'use strict';

  var OFFER = 'Free shipping on domestic orders over $199 · Excludes artwork and bulky items';
  var FREE = 'You’ve unlocked free domestic shipping 🎉';
  var STANDARD_UNDER = 11.95; // ordinary domestic parcel rate below the threshold

  function note(amount) {
    return 'Shipping is ' + amount + ' — artwork and bulky items ship at their own rate';
  }

  /* The estimate row states one of:
       "Free"      -> ASR will charge nothing
       "$50.00"    -> ASR will charge that
       "Calculating…" / "Calculated at checkout" / "No rates for 9999…"
                   -> not known yet, so nothing may be promised */
  function estimate() {
    var el = document.querySelector('.js-np-ship-result');
    if (!el) return null;
    var t = (el.textContent || '').trim();
    if (!t) return null;
    if (/^free$/i.test(t)) return { amount: 0, text: 'Free' };
    var m = t.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isNaN(n) ? null : { amount: n, text: '$' + m[1] };
  }

  /* data-np-truth records what we last wrote, so a re-render that restores the
     server copy is corrected again while an already-correct bar is left alone —
     otherwise our own writes would retrigger the observer indefinitely.
     data-np-orig preserves the server's markup (the 'near' copy contains a
     <span class="amt">) so the bar can be put back if the surcharge goes away
     without the cart re-rendering, e.g. the shopper changes postcode. */
  function write(msg, bar, state, text, flat) {
    if (msg.getAttribute('data-np-truth') === state) return;
    if (!msg.hasAttribute('data-np-orig')) msg.setAttribute('data-np-orig', msg.innerHTML);
    msg.textContent = text;
    msg.setAttribute('data-np-truth', state);
    bar.classList.toggle('np-truth-flat', !!flat);
  }

  function restore(msg, bar) {
    if (!msg.hasAttribute('data-np-truth')) return;
    if (msg.hasAttribute('data-np-orig')) msg.innerHTML = msg.getAttribute('data-np-orig');
    msg.removeAttribute('data-np-truth');
    bar.classList.remove('np-truth-flat');
  }

  function apply() {
    var bars = document.querySelectorAll('.js-cart-shipping-countdown');
    if (!bars.length) return;
    var est = estimate();

    Array.prototype.forEach.call(bars, function (bar) {
      var mode = bar.getAttribute('data-ship-mode');
      var msg = bar.querySelector('.norsu-ship__msg');
      if (!msg) return;

      if (mode === 'qualified') {
        if (!est) {
          write(msg, bar, 'pending', OFFER, false);
          bar.classList.remove('is-qualified');
        } else if (est.amount === 0) {
          write(msg, bar, 'free', FREE, false);
          bar.classList.add('is-qualified');
        } else {
          write(msg, bar, 'charged:' + est.text, note(est.text), false);
          bar.classList.remove('is-qualified');
        }
        return;
      }

      if (mode === 'near') {
        // Only a rate dearer than ordinary postage proves a surcharge is in the
        // cart; without a resolved estimate, assume none and leave the nudge.
        if (est && est.amount > STANDARD_UNDER) {
          write(msg, bar, 'charged:' + est.text, note(est.text), true);
        } else {
          restore(msg, bar);
        }
      }
    });
  }

  /* The slim treatment for a corrected 'near' bar: no eyebrow, no progress
     track, no footnote — a progress bar toward a goal the cart cannot reach is
     the thing being removed. Mirrors the .norsu-ship--far styling in
     snippets/cart-shipping-countdown.liquid. Injected from here so that snippet
     stays untouched. */
  function injectStyle() {
    if (document.getElementById('np-truth-style')) return;
    var st = document.createElement('style');
    st.id = 'np-truth-style';
    // .norsu-ship is carried in every selector purely for specificity. The
    // snippet styles the track with .cart-shipping-countdown.norsu-ship
    // .norsu-ship__track{display:block!important} — three classes and
    // !important, same as a rule without it — and its <style> is re-injected
    // after this one on every AJAX cart render, so a tie on specificity is lost
    // on document order every time. Four classes wins outright.
    var F = '.cart-shipping-countdown.norsu-ship.np-truth-flat';
    st.textContent =
      F + ' .norsu-ship__eyebrow,' +
      F + ' .norsu-ship__track,' +
      F + ' .norsu-ship__foot{display:none !important}' +
      F + '{padding:13px 18px !important;box-shadow:none !important;' +
      'background:#faf6f2 !important;border-color:#ece3db !important}' +
      F + ' .norsu-ship__msg{font-size:13px !important;' +
      'font-weight:400 !important;line-height:1.45 !important;margin:0 !important;color:#7d7176 !important}';
    (document.head || document.documentElement).appendChild(st);
  }

  /* The drawer body is replaced wholesale on every cart change and the estimate
     lands asynchronously after that, so both need watching. Assigning
     textContent swaps the text node, which is a childList mutation — no
     characterData observation required. */
  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      apply();
    }, 30);
  }

  injectStyle();
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
